/* eslint-disable max-len */
import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { SnackbarProvider } from 'notistack';
import { apm } from '@elastic/apm-rum';
import { useHistory, useLocation } from 'react-router-dom';
import { ToastDisplay } from './toaster';
import { INotifierMessage, Action, State, Dispatch, NotifierProviderProps, INotifierMessageBox } from './interfaces';
import errorDescriptions from './error-descriptions.json';
import { NotifierMessageBox } from './notifier-message-box';
import { CssBaseDecorator } from '../cssbasedecorator/css-base-decorator';
import { AccessDeniedPage } from '../../access-denied';

type IDispatchMessageBox = (title: string, message: string) => void;
const ErrorNotifierStateContext = React.createContext<{ state: State; dispatch: Dispatch; dispatchMessageBox: IDispatchMessageBox } | undefined>(undefined);

function filterOutDismissed(obj: INotifierMessage) {
  return !obj.dismissed;
}

export function notifierMessageReducer(state: State, action: Action) {
  switch (action.type) {
    case 'notify': {
      const { message = '', correlationIdentifier = '', severity = 'info', status, timestamp, warningAsInfo, hideExtraInfo, helpLink, messageTitle } = action.payload;
      let { detail = '', title = '' } = action.payload;

      const errorDescription = errorDescriptions.find(x => x.key.toLowerCase() === title.toLowerCase());
      title = errorDescription?.title || title;
      detail = errorDescription?.detail || detail;

      // Check for existing
      const correlationIdentifierArr = state.errors.map(x => x.correlationIdentifier);
      if (correlationIdentifier !== '' && correlationIdentifierArr.indexOf(correlationIdentifier) > -1) return state;

      // If BusinessException and severity == info || warning
      if (status === 400 && severity !== 'error') {
        const notifierMessageBox: INotifierMessageBox = {
          id: uuidv4(),
          detail,
          title,
          correlationIdentifier,
          severity,
          status,
          warningAsInfo,
          hideExtraInfo,
        };
        return { message: notifierMessageBox, errors: [...state.errors], toasts: [...state.toasts], isAccessDenied: state.isAccessDenied };
      }

      let detailedMessage = `${title}${detail ? ` - ${detail}` : ''}`;
      detailedMessage = detailedMessage || message;
      if (severity === 'info' || severity === 'success') {
        const newToast = {
          id: uuidv4(),
          message,
          severity,
        };
        return { errors: [...state.errors], toasts: [...state.toasts, newToast], message: state.message, isAccessDenied: state.isAccessDenied };
      }
      if (status === 401) {
        // Return old state if there's already a 401 Unauthorized error visible
        if (state.errors.find((err: INotifierMessage) => err.status && err.status === 401))
          return { errors: [...state.errors], toasts: [...state.toasts], message: state.message, isAccessDenied: state.isAccessDenied };
        // else just continue

        detailedMessage = 'Du er ikke logget ind i Fasit. Log ind igen. Hvis du fortsat har problemer skal du kontakte din systemadministrator.';
      }
      if (status === 403) {
        // Check if there's already a 403 error visible
        const existing403 = state.errors.find((err: INotifierMessage) => err.status === 403 && !err.dismissed);

        // If there's already a 403 error visible, return existing state to prevent duplicates
        if (existing403) {
          return {
            errors: [...state.errors],
            toasts: [...state.toasts],
            message: state.message,
            isAccessDenied: true,
          };
        }
        // This is the first 403 - set the message and will block the page
        detailedMessage =
          'Handlingen kan ikke udføres. Det skyldes, at du ikke har de nødvendige sikkerhedsroller til at udføre den valgte handling, eller at posten ikke længere findes. Kontakt din systemadministrator.';
      }

      // Specific DFDG errors
      if (messageTitle) {
        const newMessage: INotifierMessageBox = {
          id: uuidv4(),
          title: messageTitle,
          detail,
          helpLink,
        };
        return { errors: [...state.errors], toasts: [...state.toasts], message: newMessage, isAccessDenied: state.isAccessDenied };
      }

      if (detailedMessage === '' && correlationIdentifier === '')
        return { errors: [...state.errors], toasts: [...state.toasts], message: state.message, isAccessDenied: state.isAccessDenied };

      const newError: INotifierMessage = {
        id: uuidv4(),
        message: detailedMessage,
        correlationIdentifier,
        severity,
        status,
        timestamp,
      };

      apm.captureError({ ...newError, name: title });

      return {
        errors: [...state.errors.filter(filterOutDismissed), newError],
        toasts: [...state.toasts],
        message: state.message,
        isAccessDenied: status === 403 ? true : state.isAccessDenied,
      };
    }
    case 'dismiss': {
      const newErrors = state.errors.map((msg: INotifierMessage) => {
        const msgCopy = { ...msg };
        if (msgCopy.id === action.payload.id) {
          msgCopy.dismissed = true;
        }
        return msgCopy;
      });
      const newToasts = state.toasts.filter((msg: INotifierMessage) => msg.id !== action.payload.id);
      return { errors: [...newErrors], toasts: [...newToasts], message: state.message?.id === action.payload?.id ? undefined : state.message };
    }
    case 'dismissAll': {
      const newErrors = state.errors.map((msg: INotifierMessage) => {
        const msgCopy = { ...msg };
        msgCopy.dismissed = true;
        return msgCopy;
      });
      const newToasts = state.toasts.filter((msg: INotifierMessage) => msg.id !== action.payload.id);
      return { errors: [...newErrors], toasts: [...newToasts], message: state.message?.id === action.payload?.id ? undefined : state.message };
    }
    case 'clearAccessDenied': {
      return {
        ...state,
        isAccessDenied: false,
        errors: [],
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function NotifierProvider({ children }: NotifierProviderProps) {
  const [state, dispatch] = React.useReducer(notifierMessageReducer, {
    errors: [],
    toasts: [],
    message: undefined,
    isAccessDenied: false,
  });

  const { errors, toasts, message, isAccessDenied } = state;

  const location = useLocation();
  const history = useHistory();

  // Clear block when URL changes
  React.useEffect(() => {
    if (isAccessDenied) {
      dispatch({ type: 'clearAccessDenied' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  function dispatchMessageBox(title: string, message: string) {
    dispatch({ type: 'notify', payload: { detail: message, status: 400, title } });
  }

  const value = React.useMemo(() => ({ state, dispatch, dispatchMessageBox }), [state, dispatch, dispatchMessageBox]);

  const error403 = errors.find(msg => msg.status === 403 && !msg.dismissed);

  return (
    <CssBaseDecorator>
      <SnackbarProvider
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        maxSnack={3}
      >
        <NotifierMessageBox message={message} dispatch={dispatch} />
        <ToastDisplay toasts={toasts} dispatch={dispatch} />
        <ErrorNotifierStateContext.Provider value={value}>
          <>
            <AccessDeniedPage
              show={isAccessDenied === true}
              onNavigateHome={() => {
                dispatch({ type: 'clearAccessDenied' });
                history.push('/');
              }}
              message={error403?.message || 'Adgang nægtet'}
              correlationIdentifier={error403?.correlationIdentifier}
              timestamp={error403?.timestamp}
            />
            <span style={{ display: isAccessDenied === true ? 'none' : '' }}>{children}</span>
          </>
        </ErrorNotifierStateContext.Provider>
      </SnackbarProvider>
    </CssBaseDecorator>
  );
}

function useNotifier() {
  const context = React.useContext(ErrorNotifierStateContext);
  if (context === undefined) {
    throw new Error('useNotifier must be used within a NotifierProvider');
  }
  return context;
}

export { NotifierProvider, useNotifier };
